import { useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import Alert from "@mui/material/Alert";
import Link from "@mui/material/Link";
import { forgetPassword } from "@/lib/auth-client";

export function ForgotPasswordPage() {
  const [email, setEmail] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      // Use Better Auth client method for password reset
      const result = await forgetPassword({
        email,
        redirectTo: `${window.location.origin}/reset-password`,
      });

      if (result.error) {
        setError(result.error.message || "Failed to send reset email");
        setLoading(false);
        return;
      }

      setSuccess(true);
      setLoading(false);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Failed to send reset email",
      );
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        bgcolor: "grey.50",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        p: 2,
      }}
    >
      <Card sx={{ maxWidth: 400, width: "100%", p: { xs: 3, md: 4 } }}>
        <Box sx={{ display: "flex", justifyContent: "center", mb: 3 }}>
          <img
            src="/logos/agent-edge.png"
            alt="Agent Edge"
            style={{ height: 48, width: "auto" }}
          />
        </Box>
        <Typography
          variant="h5"
          fontWeight={700}
          textAlign="center"
          sx={{ mb: 1 }}
        >
          Forgot Password
        </Typography>

        <Typography
          variant="body2"
          color="text.secondary"
          textAlign="center"
          sx={{ mb: 3 }}
        >
          Enter your email address and we&apos;ll send you a link to reset your
          password.
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {success ? (
          <Alert severity="success" sx={{ mb: 2 }}>
            Check your email for a password reset link. If you don&apos;t see
            it, check your spam folder.
          </Alert>
        ) : (
          <form onSubmit={handleSubmit}>
            <TextField
              label="Email"
              type="email"
              fullWidth
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              sx={{ mb: 3 }}
              required
              autoComplete="email"
            />
            <Button
              type="submit"
              variant="contained"
              fullWidth
              size="large"
              disabled={loading}
            >
              {loading ? "Sending..." : "Send Reset Link"}
            </Button>
          </form>
        )}

        <Box sx={{ mt: 2, textAlign: "center" }}>
          <Link
            component={RouterLink}
            to="/login"
            underline="hover"
            sx={{ display: "inline-block", py: 1, px: 2 }}
          >
            Back to Sign In
          </Link>
        </Box>
      </Card>
    </Box>
  );
}
